import { Suspense, lazy } from 'react'

import { Navigate, RouterProvider, createBrowserRouter } from 'react-router-dom'

import { AppLayout } from '@/components/layout/AppLayout'
import { LoadingScreen } from '@/components/common/LoadingScreen'
import { ROUTES } from '@/config/routes'
import { ProtectedRoute } from '@/routes/ProtectedRoute'
import { PermissionGuard } from '@/routes/PermissionGuard'

const LoginPage = lazy(() =>
  import('@/features/auth/pages/LoginPage').then((module) => ({
    default: module.LoginPage,
  })),
)
const DashboardPage = lazy(() =>
  import('@/features/dashboard/pages/DashboardPage').then((module) => ({
    default: module.DashboardPage,
  })),
)
const AnalyticsPage = lazy(() =>
  import('@/features/analytics/pages/AnalyticsPage').then((module) => ({
    default: module.AnalyticsPage,
  })),
)
const ConversationsWorkspace = lazy(() =>
  import('@/features/conversations/pages/ConversationsWorkspace').then((module) => ({
    default: module.ConversationsWorkspace,
  })),
)
const MyChatsWorkspace = lazy(() =>
  import('@/features/conversations/pages/MyChatsWorkspace').then((module) => ({
    default: module.MyChatsWorkspace,
  })),
)
const UsersPage = lazy(() =>
  import('@/features/users/pages/UsersPage').then((module) => ({
    default: module.UsersPage,
  })),
)
const RolesPage = lazy(() =>
  import('@/features/roles/pages/RolesPage').then((module) => ({
    default: module.RolesPage,
  })),
)
const AgentsPage = lazy(() =>
  import('@/features/agents/pages/AgentsPage').then((module) => ({
    default: module.AgentsPage,
  })),
)
const WidgetsPage = lazy(() =>
  import('@/features/widgets/pages/WidgetsPage').then((module) => ({
    default: module.WidgetsPage,
  })),
)
const AiManagementPage = lazy(() =>
  import('@/features/ai/pages/AiManagementPage').then((module) => ({
    default: module.AiManagementPage,
  })),
)
const KnowledgeBasePage = lazy(() =>
  import('@/features/knowledge-base/pages/KnowledgeBasePage').then((module) => ({
    default: module.KnowledgeBasePage,
  })),
)
const SettingsPage = lazy(() =>
  import('@/features/dashboard/pages/SettingsPage').then((module) => ({
    default: module.SettingsPage,
  })),
)
const IntegrationSettingsPage = lazy(() =>
  import('@/features/settings/pages/IntegrationSettingsPage').then((module) => ({
    default: module.IntegrationSettingsPage,
  })),
)
const FeedbacksPage = lazy(() =>
  import('@/features/feedbacks/pages/FeedbacksPage').then((module) => ({
    default: module.FeedbacksPage,
  })),
)
const DatabaseCleanupPage = lazy(() =>
  import('@/features/settings/pages/DatabaseCleanupPage').then((module) => ({
    default: module.DatabaseCleanupPage,
  })),
)

const router = createBrowserRouter([
  {
    path: ROUTES.login,
    element: (
      <Suspense fallback={<LoadingScreen />}>
        <LoginPage />
      </Suspense>
    ),
  },
  {
    element: <ProtectedRoute />,
    children: [
      {
        element: <AppLayout />,
        children: [
          {
            index: true,
            element: <Navigate to={ROUTES.dashboard} replace />,
          },
          {
            path: ROUTES.dashboard.replace('/', ''),
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <DashboardPage />
              </Suspense>
            ),
          },
          {
            path: ROUTES.analytics.replace('/', ''),
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <PermissionGuard permission="analytics:read">
                  <AnalyticsPage />
                </PermissionGuard>
              </Suspense>
            ),
          },
          {
            path: ROUTES.conversations.replace('/', ''),
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <ConversationsWorkspace />
              </Suspense>
            ),
          },
          {
            path: 'conversations/:conversationId',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <ConversationsWorkspace />
              </Suspense>
            ),
          },
          {
            path: ROUTES.myChats.replace('/', ''),
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <MyChatsWorkspace />
              </Suspense>
            ),
          },
          {
            path: ROUTES.users.replace('/', ''),
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <PermissionGuard permission="users:read">
                  <UsersPage />
                </PermissionGuard>
              </Suspense>
            ),
          },
          {
            path: ROUTES.roles.replace('/', ''),
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <PermissionGuard permission="roles:read">
                  <RolesPage />
                </PermissionGuard>
              </Suspense>
            ),
          },
          {
            path: ROUTES.agents.replace('/', ''),
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <PermissionGuard permission="agents:read">
                  <AgentsPage />
                </PermissionGuard>
              </Suspense>
            ),
          },
          {
            path: ROUTES.widgets.replace('/', ''),
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <PermissionGuard permission="widgets:read">
                  <WidgetsPage />
                </PermissionGuard>
              </Suspense>
            ),
          },
          {
            path: ROUTES.ai.replace('/', ''),
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <PermissionGuard permission="ai:read">
                  <AiManagementPage />
                </PermissionGuard>
              </Suspense>
            ),
          },
          {
            path: ROUTES.knowledgeBase.replace('/', ''),
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <PermissionGuard permission="knowledge-base:read">
                  <KnowledgeBasePage />
                </PermissionGuard>
              </Suspense>
            ),
          },
          {
            path: ROUTES.integrations.replace('/', ''),
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <PermissionGuard permission="integrations:read">
                  <IntegrationSettingsPage />
                </PermissionGuard>
              </Suspense>
            ),
          },
          {
            path: ROUTES.feedbacks.replace('/', ''),
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <PermissionGuard permission="feedbacks:read">
                  <FeedbacksPage />
                </PermissionGuard>
              </Suspense>
            ),
          },
          {
            path: ROUTES.settings.replace('/', ''),
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <PermissionGuard permission="settings:update">
                  <SettingsPage />
                </PermissionGuard>
              </Suspense>
            ),
          },
          {
            path: ROUTES.databaseCleanup.replace('/', ''),
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <PermissionGuard permission="settings:update">
                  <DatabaseCleanupPage />
                </PermissionGuard>
              </Suspense>
            ),
          },
        ],
      },
    ],
  },
  {
    path: '*',
    element: <Navigate to={ROUTES.dashboard} replace />,
  },
])

export const AppRouter = () => <RouterProvider router={router} />
